import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, GraduationCap, CreditCard, ChevronRight, Trash2, ArrowLeft, Check, X, Receipt, Mail, Phone, MapPin, ArrowRightLeft, Pencil, Plus, Save, Home, Search } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "En attente", color: "bg-amber-100 text-amber-800" },
  paid: { label: "Payé", color: "bg-emerald-100 text-emerald-800" },
  partial: { label: "Partiel", color: "bg-sky-100 text-sky-800" },
  failed: { label: "Échec", color: "bg-red-100 text-red-700" },
};
const MODE_LABELS = {
  cheque: "Chèque",
  especes: "Espèces",
  card: "Carte bancaire",
  monthly: "Prélèvement mensuel",
  pass_plus: "Pass+ / Pass Région",
};

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || { label: status || "—", color: "bg-stone-100 text-stone-600" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
};

const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

// ============================ Families ============================
export const FamiliesAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editFamily, setEditFamily] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [addStudent, setAddStudent] = useState(false);
  const [showOnlyDue, setShowOnlyDue] = useState(false);
  const [q, setQ] = useState("");

  const norm = (v) => (v || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filteredItems = useMemo(() => {
    const needle = norm(q).trim();
    return items.filter((f) => {
      if (showOnlyDue && (f.remainingAmount || 0) <= 0.01) return false;
      if (!needle) return true;
      return norm([f.parentName, f.email, f.phone, f.city, f.postalCode, (f.studentNames || []).join(" ")].join(" ")).includes(needle);
    });
  }, [items, q, showOnlyDue]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/families");
      setItems(data || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (fam) => {
    setSelected(fam.id);
    try {
      const { data } = await api.get(`/admin/families/${fam.id}`);
      setDetail(data);
    } catch { toast.error("Erreur"); setSelected(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement cette famille (et toutes ses inscriptions/paiements) ?")) return;
    try {
      await api.delete(`/admin/families/${id}`);
      toast.success("Famille supprimée");
      setSelected(null); setDetail(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  const delStudent = async (s) => {
    if (!window.confirm(`Supprimer ${s.firstName} ${s.lastName || ""} et toutes ses inscriptions ?`)) return;
    try {
      await api.delete(`/admin/students/${s.id}`);
      toast.success("Élève supprimé");
      const fam = items.find((f) => f.id === selected);
      if (fam) openDetail(fam);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (selected && detail) {
    const { family, students, enrollments, payments, orders = [], summary = {} } = detail;
    return (
      <div>
        <button onClick={() => { setSelected(null); setDetail(null); }} className="inline-flex items-center gap-1 text-stone-600 hover:text-deep-rose mb-4 text-sm">
          <ArrowLeft size={14} /> Retour aux familles
        </button>
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">{family.parentName}</h1>
              <div className="text-stone-500 text-sm mt-2 space-y-1">
                <div className="flex items-center gap-2"><Mail size={14} /> {family.email}</div>
                {family.phone && <div className="flex items-center gap-2"><Phone size={14} /> {family.phone}</div>}
                {(family.address || family.postalCode || family.city) && (
                  <div className="flex items-center gap-2"><Home size={14} /> {[family.address, family.postalCode, family.city].filter(Boolean).join(" · ")}</div>
                )}
                <div className="text-stone-400">Créée le {fmtDate(family.createdAt)}</div>
              </div>
              {family.consents && (
                <div className="mt-3 pt-3 border-t border-stone-100 text-xs" data-testid="family-consents">
                  <div className="uppercase tracking-widest text-stone-500 mb-1.5">Consentements signés</div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${family.consents.cgv ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{family.consents.cgv ? "✓" : "○"} CGV</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${family.consents.reglement ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{family.consents.reglement ? "✓" : "○"} Règlement</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${family.consents.image ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{family.consents.image ? "✓" : "○"} Droit image</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${family.consents.whatsapp ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{family.consents.whatsapp ? "✓" : "○"} WhatsApp</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${family.consents.cseAttestation ? "bg-amber-50 text-amber-700 font-medium" : "bg-stone-100 text-stone-500"}`}>{family.consents.cseAttestation ? "★" : "○"} Attestation CSE</span>
                  </div>
                  {family.consents.acceptedAt && (
                    <div className="text-stone-400 mt-1.5">Accepté le {fmtDate(family.consents.acceptedAt)}{family.consents.season ? ` · saison ${family.consents.season}` : ""}</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditFamily(family)} data-testid="family-edit-button" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs">
                <Pencil size={14}/> Modifier
              </button>
              <button onClick={() => del(family.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"><Trash2 size={16}/></button>
            </div>
          </div>
        </div>

        {orders.length > 0 && (
          <>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="text-xs uppercase tracking-widest text-stone-500">Total commandé</div>
                <div className="font-serif-display text-2xl text-stone-800 mt-1">{fmtEur(summary.totalFinal || 0)}</div>
                <div className="text-xs text-stone-400 mt-0.5">{summary.ordersCount || orders.length} commande{(summary.ordersCount || orders.length) > 1 ? "s" : ""}</div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="text-xs uppercase tracking-widest text-emerald-700">Déjà encaissé</div>
                <div className="font-serif-display text-2xl text-emerald-700 mt-1">{fmtEur(summary.totalPaid || 0)}</div>
              </div>
              <div className={`rounded-2xl p-4 shadow-sm ${(summary.remainingAmount || 0) > 0.01 ? "bg-amber-50" : "bg-white"}`}>
                <div className={`text-xs uppercase tracking-widest ${(summary.remainingAmount || 0) > 0.01 ? "text-amber-700" : "text-stone-500"}`}>Reste à percevoir</div>
                <div className={`font-serif-display text-2xl mt-1 ${(summary.remainingAmount || 0) > 0.01 ? "text-amber-800" : "text-stone-500"}`}>{fmtEur(summary.remainingAmount || 0)}</div>
              </div>
            </div>

            <h2 className="font-serif-display text-xl text-stone-800 mt-8 mb-3">Commandes ({orders.length})</h2>
            <div className="space-y-2" data-testid="family-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid={`family-order-${o.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-stone-500">{o.orderNumber || o.id?.slice(0, 8)}</div>
                    <div className="text-stone-800 text-sm mt-0.5">
                      {fmtDate(o.createdAt)} · {MODE_LABELS[o.paymentMode] || o.paymentMode}
                      {o.type === "stage" && <span className="ml-2 text-[10px] uppercase tracking-widest bg-blush-light text-deep-rose px-2 py-0.5 rounded-full">Stage</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif-display text-stone-800">{fmtEur(o.totalFinal)}</div>
                    <div className="text-xs text-stone-500">payé : <span className="text-emerald-700 font-medium">{fmtEur(o.totalPaid)}</span></div>
                    {(o.remainingAmount || 0) > 0.01 && (
                      <div className="text-xs text-amber-700 font-medium">reste : {fmtEur(o.remainingAmount)}</div>
                    )}
                  </div>
                  <StatusBadge status={o.paymentStatus} />
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex items-center justify-between mt-8 mb-3 gap-2 flex-wrap">
          <h2 className="font-serif-display text-xl text-stone-800">Élèves & créneaux ({students.length})</h2>
          <button onClick={() => setAddStudent(true)} data-testid="student-add-button" className="inline-flex items-center gap-1.5 text-xs bg-coral text-white px-3 py-1.5 rounded-full">
            <Plus size={12}/> Ajouter un élève
          </button>
        </div>        <div className="space-y-4">
          {students.map((s) => {
            const studentEnrollments = enrollments.filter((e) => e.studentId === s.id);
            return (
              <StudentEnrollmentBlock
                key={s.id}
                student={s}
                enrollments={studentEnrollments}
                onEdit={() => setEditStudent(s)}
                onDelete={() => delStudent(s)}
                onChange={() => openDetail(family)}
              />
            );
          })}
          {students.length === 0 && (
            <div className="bg-white rounded-xl p-6 text-center text-stone-500 text-sm">Aucun élève. Cliquez sur « Ajouter un élève ».</div>
          )}
          {(() => {
            const orphan = enrollments.filter((e) => !students.some((s) => s.id === e.studentId));
            if (!orphan.length) return null;
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-widest text-amber-700 mb-2">Inscriptions sans élève rattaché</div>
                <EnrollmentsList enrollments={orphan} onChange={() => openDetail(family)} showStudent />
              </div>
            );
          })()}
        </div>

        <h2 className="font-serif-display text-xl text-stone-800 mt-8 mb-3">Paiements en ligne ({payments.length})</h2>
        <PaymentsList payments={payments} />

        {editFamily && (
          <FamilyEditModal
            family={editFamily}
            onClose={() => setEditFamily(null)}
            onSaved={() => { setEditFamily(null); openDetail(family); }}
          />
        )}
        {editStudent && (
          <StudentEditModal
            student={editStudent}
            familyId={family.id}
            onClose={() => setEditStudent(null)}
            onSaved={() => { setEditStudent(null); openDetail(family); }}
          />
        )}
        {addStudent && (
          <StudentEditModal
            student={null}
            familyId={family.id}
            onClose={() => setAddStudent(false)}
            onSaved={() => { setAddStudent(false); openDetail(family); }}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Familles</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Comptes parents avec leurs élèves et inscriptions.</p>
        </div>
      </div>
      {loading ? <div className="text-stone-500 p-8 text-center">Chargement...</div> : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-stone-500">Aucune famille pour le moment.</div>
      ) : (
        <>
          {(() => {
            const openBalance = items.filter((f) => (f.remainingAmount || 0) > 0.01);
            const totalRemaining = openBalance.reduce((s, f) => s + (f.remainingAmount || 0), 0);
            return openBalance.length > 0 ? (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-4 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="families-open-balance-summary">
                <div className="text-sm">
                  <strong>{openBalance.length}</strong> famille{openBalance.length > 1 ? "s" : ""} avec solde ouvert · <strong>{fmtEur(totalRemaining)}</strong> à percevoir au total
                </div>
                <button onClick={() => setShowOnlyDue((v) => !v)} data-testid="families-toggle-due" className="text-xs bg-amber-800 hover:bg-amber-900 text-white px-3 py-1.5 rounded-full">
                  {showOnlyDue ? "Voir toutes les familles" : "Filtrer les impayés"}
                </button>
              </div>
            ) : null;
          })()}
          <div className="bg-white rounded-2xl shadow-sm p-3 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher une famille : parent, élève, email, téléphone, ville…"
                data-testid="families-search"
                className="w-full bg-stone-50 border border-stone-200 rounded-lg pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
              />
              {q && (
                <button onClick={() => setQ("")} data-testid="families-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-stone-200 text-stone-400">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="text-sm text-stone-500 shrink-0 sm:px-2" data-testid="families-count">
              {filteredItems.length} / {items.length} famille{items.length > 1 ? "s" : ""}
            </div>
          </div>
          <div className="space-y-2">
            {filteredItems
              .map((f) => {
                const remaining = f.remainingAmount || 0;
                return (
                  <button key={f.id} onClick={() => openDetail(f)} data-testid={`family-row-${f.id}`} className="w-full text-left bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 rounded-full bg-blush-light flex items-center justify-center text-deep-rose"><Users size={18} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif-display text-stone-800 truncate">{f.parentName}</div>
                      <div className="text-stone-500 text-sm truncate">{f.email}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 text-stone-500 text-xs shrink-0">
                      <span>{f.studentsCount} élève{f.studentsCount > 1 ? "s" : ""}</span>
                      <span>{f.enrollmentsCount} inscr.</span>
                    </div>
                    <div className="hidden md:flex flex-col items-end shrink-0 min-w-[110px]">
                      <div className="text-xs text-stone-400 uppercase tracking-widest">Commandé</div>
                      <div className="font-serif-display text-sm text-stone-700">{fmtEur(f.totalFinal || 0)}</div>
                    </div>
                    <div className={`flex flex-col items-end shrink-0 min-w-[110px] px-3 py-1.5 rounded-lg ${remaining > 0.01 ? "bg-amber-50" : ""}`}>
                      <div className={`text-xs uppercase tracking-widest ${remaining > 0.01 ? "text-amber-700" : "text-stone-400"}`}>Reste dû</div>
                      <div className={`font-serif-display text-sm ${remaining > 0.01 ? "text-amber-800 font-semibold" : "text-emerald-700"}`}>
                        {remaining > 0.01 ? fmtEur(remaining) : "—"}
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-stone-300 shrink-0" />
                  </button>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

// ============================ Enrollments ============================
const DAY_LABELS_CRM = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
};
const DAY_ORDER_CRM = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const EnrollmentsList = ({ enrollments, onChange, showStudent = true }) => {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ paymentStatus: "", paidAmount: 0 });
  const [moving, setMoving] = useState(null); // enrollment id being moved
  const [targetSlotId, setTargetSlotId] = useState("");
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    api.get("/admin/schedule_slots").then(({ data }) => setSlots(data || [])).catch(() => {});
  }, []);

  const slotById = (id) => slots.find((s) => s.id === id);

  const saveEdit = async (eid) => {
    try {
      await api.patch(`/admin/enrollments/${eid}`, {
        paymentStatus: draft.paymentStatus,
        paidAmount: Number(draft.paidAmount || 0),
      });
      toast.success("Mise à jour effectuée");
      setEditing(null);
      onChange && onChange();
    } catch { toast.error("Erreur"); }
  };

  const doMove = async (eid) => {
    if (!targetSlotId) { toast.error("Sélectionnez un créneau cible"); return; }
    try {
      await api.patch(`/admin/enrollments/${eid}`, { slotId: targetSlotId });
      toast.success("Inscription déplacée vers le nouveau créneau");
      setMoving(null); setTargetSlotId("");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du déplacement");
    }
  };

  const del = async (eid) => {
    if (!window.confirm("Supprimer cette inscription ?")) return;
    try {
      await api.delete(`/admin/enrollments/${eid}`);
      toast.success("Inscription supprimée");
      onChange && onChange();
    } catch { toast.error("Erreur"); }
  };

  if (enrollments.length === 0) {
    return <div className="bg-white rounded-xl p-6 text-center text-stone-500 text-sm">Aucune inscription.</div>;
  }

  // Slots grouped by day for the picker, sorted by day order then startTime
  const slotOptions = DAY_ORDER_CRM
    .map((d) => ({ day: d, label: DAY_LABELS_CRM[d], items: slots.filter((s) => s.day === d).sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-2">
      {enrollments.map((e) => {
        // Slot info — fall back to legacy course display when this enrollment was created
        // via the old course-based flow (no slotId).
        const hasSlot = !!e.slotId;
        const dayLabel = e.slotDay ? (DAY_LABELS_CRM[e.slotDay] || e.slotDay) : "";
        const slotTitle = e.slotTitle || e.courseName || "(créneau inconnu)";
        const slotTeacher = hasSlot ? (slotById(e.slotId)?.teacherName || "") : "";
        const slotRoom = hasSlot ? (slotById(e.slotId)?.room || "") : "";
        return (
        <div key={e.id} className="bg-white rounded-xl shadow-sm p-4" data-testid={`enrollment-row-${e.id}`}>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-serif-display text-stone-800 flex items-center gap-2 flex-wrap">
                {showStudent && <span className="text-stone-500 mr-1">{e.studentFirstName} —</span>}
                <span>{slotTitle}</span>
                {hasSlot && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-blush-light text-deep-rose">
                    {dayLabel} {e.slotStartTime}–{e.slotEndTime}
                  </span>
                )}
              </div>
              <div className="text-stone-500 text-xs mt-1">
                {hasSlot && slotTeacher && <span>{slotTeacher} · </span>}
                {hasSlot && slotRoom && <span>{slotRoom} · </span>}
                Saison {e.season} · Inscrit le {fmtDate(e.createdAt)}
                {e.weeklyMinutes ? <span> · {Math.floor(e.weeklyMinutes / 60)}h{e.weeklyMinutes % 60 ? String(e.weeklyMinutes % 60).padStart(2, "0") : ""} hebdo</span> : null}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-serif-display text-stone-800">{fmtEur(e.finalAmount)}</div>
              {e.discountPercent > 0 && (
                <div className="text-emerald-600 text-xs">remise {e.discountPercent}%</div>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
            <span className="text-stone-500">Mode :</span>
            <span className="font-medium text-stone-700">{MODE_LABELS[e.paymentMode] || e.paymentMode}</span>
            <span className="text-stone-300">·</span>
            <StatusBadge status={e.paymentStatus} />
            <span className="text-stone-500">Payé : {fmtEur(e.paidAmount)} / {fmtEur(e.finalAmount)}</span>
          </div>
          {editing === e.id ? (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <select value={draft.paymentStatus} onChange={(ev) => setDraft({ ...draft, paymentStatus: ev.target.value })} className="bg-stone-50 border border-stone-200 rounded px-2 py-1.5 text-sm">
                <option value="">— Statut —</option>
                <option value="pending">En attente</option>
                <option value="paid">Payé</option>
                <option value="partial">Partiel</option>
                <option value="failed">Échec</option>
              </select>
              <input type="number" value={draft.paidAmount} onChange={(ev) => setDraft({ ...draft, paidAmount: ev.target.value })} placeholder="Montant reçu (€)" className="bg-stone-50 border border-stone-200 rounded px-2 py-1.5 text-sm" />
              <div className="flex gap-2">
                <button onClick={() => saveEdit(e.id)} className="flex-1 inline-flex items-center justify-center gap-1 bg-coral text-white rounded px-2 py-1.5 text-xs"><Check size={12}/> Enregistrer</button>
                <button onClick={() => setEditing(null)} className="flex-1 inline-flex items-center justify-center gap-1 bg-stone-200 text-stone-700 rounded px-2 py-1.5 text-xs"><X size={12}/> Annuler</button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={() => { setEditing(e.id); setDraft({ paymentStatus: e.paymentStatus, paidAmount: e.paidAmount || 0 }); }} className="text-xs px-3 py-1 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700" data-testid={`enrollment-pay-${e.id}`}>Marquer comme payé</button>
              <button onClick={() => { setMoving(e.id); setTargetSlotId(""); }} className="text-xs px-3 py-1 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-700 inline-flex items-center gap-1.5" data-testid={`enrollment-move-${e.id}`}><ArrowRightLeft size={12}/> Changer de créneau</button>
              <button onClick={() => del(e.id)} className="text-xs px-3 py-1 rounded-full bg-red-50 hover:bg-red-100 text-red-600" data-testid={`enrollment-delete-${e.id}`}>Supprimer</button>
            </div>
          )}
          {moving === e.id && (
            <div className="mt-3 bg-sky-50 border border-sky-100 rounded-xl p-3">
              <div className="text-xs uppercase tracking-widest text-sky-700 mb-2">Déplacer {e.studentFirstName} vers :</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select value={targetSlotId} onChange={(ev) => setTargetSlotId(ev.target.value)} className="flex-1 bg-white border border-stone-200 rounded px-2 py-1.5 text-sm" data-testid={`enrollment-target-${e.id}`}>
                  <option value="">— Choisir un créneau —</option>
                  {slotOptions.map((g) => (
                    <optgroup key={g.day} label={g.label}>
                      {g.items.filter((s) => s.id !== e.slotId).map((s) => (
                        <option key={s.id} value={s.id}>{s.startTime}–{s.endTime} · {s.title}{s.teacherName ? ` (${s.teacherName})` : ""}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <button onClick={() => doMove(e.id)} className="inline-flex items-center justify-center gap-1.5 bg-sky-600 text-white rounded-full px-4 py-1.5 text-xs" data-testid={`enrollment-move-confirm-${e.id}`}><Check size={12}/> Confirmer</button>
                <button onClick={() => { setMoving(null); setTargetSlotId(""); }} className="inline-flex items-center justify-center gap-1.5 bg-stone-200 text-stone-700 rounded-full px-4 py-1.5 text-xs"><X size={12}/> Annuler</button>
              </div>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

const PaymentsList = ({ payments }) => {
  if (payments.length === 0) return <div className="bg-white rounded-xl p-6 text-center text-stone-500 text-sm">Aucun paiement en ligne.</div>;
  return (
    <div className="space-y-2">
      {payments.map((p) => (
        <div key={p.id} className="bg-white rounded-xl shadow-sm p-4" data-testid={`payment-row-${p.id}`}>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><CreditCard size={18}/></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-serif-display text-stone-800">{fmtEur(p.amount)}</span>
                <span className="text-stone-400 text-sm">{p.description}</span>
              </div>
              {(p.parentName || p.email) && (
                <div className="text-stone-700 text-sm mt-1">
                  {p.parentName && <span className="font-medium">{p.parentName}</span>}
                  {p.email && <span className="text-stone-500"> · {p.email}</span>}
                  {p.phone && <span className="text-stone-500"> · {p.phone}</span>}
                </div>
              )}
              {Array.isArray(p.students) && p.students.length > 0 && (
                <div className="text-stone-500 text-xs mt-1">
                  {p.students.map((s, idx) => (
                    <span key={idx}>{idx > 0 ? " · " : ""}<span className="text-stone-700">{s.name}</span>{s.course ? <span className="text-stone-400"> ({s.course})</span> : ""}</span>
                  ))}
                </div>
              )}
              <div className="text-stone-400 text-xs mt-1">{fmtDate(p.createdAt)} · {MODE_LABELS[p.mode] || p.mode}
                {p.mode === "monthly" && p.iterations ? ` · ${p.iterationsPaid || 0}/${p.iterations} prélèvements` : ""}
              </div>
            </div>
            <StatusBadge status={p.paymentStatus === "paid" ? "paid" : (p.paymentStatus === "unpaid" ? "pending" : p.paymentStatus)} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================ Per-student block (used by FamilyDetail + EnrollmentsAdmin) ============================
const StudentEnrollmentBlock = ({ student, enrollments, onEdit, onDelete, onChange, compact = false }) => {
  const totalFinal = enrollments.reduce((sum, e) => sum + (e.finalAmount || 0), 0);
  const totalPaid = enrollments.reduce((sum, e) => sum + (e.paidAmount || 0), 0);
  const allPaid = enrollments.length > 0 && enrollments.every((e) => e.paymentStatus === "paid");
  const anyPending = enrollments.some((e) => e.paymentStatus === "pending" || e.paymentStatus === "partial");
  const headerBadge = allPaid ? "paid" : anyPending ? "pending" : (enrollments[0]?.paymentStatus || "pending");

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`student-block-${student?.id || enrollments[0]?.studentId}`}>
      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blush-light/50 to-transparent border-b border-blush-light">
        <div className="w-10 h-10 rounded-full bg-blush-light flex items-center justify-center text-deep-rose shrink-0"><GraduationCap size={18} /></div>
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-lg text-stone-800">
            {student ? `${student.firstName} ${student.lastName || ""}` : (enrollments[0]?.studentFirstName || "Élève inconnu")}
          </div>
          <div className="text-stone-500 text-xs">
            {student?.age ? `${student.age} ans` : ""}
            {student?.age && student?.birthDate ? " · " : ""}
            {student?.birthDate || ""}
            {enrollments.length > 0 && (
              <>
                {(student?.age || student?.birthDate) ? " · " : ""}
                {enrollments.length} créneau{enrollments.length > 1 ? "x" : ""}
              </>
            )}
            {student?.notes ? ` · ${student.notes}` : ""}
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end shrink-0" title="Total des cours de cet élève (hors cotisation & FFD)">
          <div className="font-serif-display text-stone-800">{fmtEur(totalFinal)}</div>
          <div className="text-xs text-stone-500">cours · payé : {fmtEur(totalPaid)}</div>
        </div>
        {enrollments.length > 0 && <StatusBadge status={headerBadge} />}
        {onEdit && (
          <button onClick={onEdit} data-testid={`student-edit-${student?.id}`} className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 shrink-0"><Pencil size={14}/></button>
        )}
        {onDelete && (
          <button onClick={onDelete} data-testid={`student-delete-${student?.id}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 shrink-0"><Trash2 size={14}/></button>
        )}
      </div>
      <div className="p-4">
        {enrollments.length === 0 ? (
          <div className="text-center text-stone-400 text-xs italic py-2">Aucun créneau pour cet élève.</div>
        ) : (
          <EnrollmentsList enrollments={enrollments} onChange={onChange} showStudent={false} />
        )}
      </div>
    </div>
  );
};


// ============================ All enrollments view (grouped by student) ============================
export const EnrollmentsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enrollments");
      setItems(data || []);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items
    .filter((e) => filter === "all" || e.paymentStatus === filter)
    .filter((e) => dayFilter === "all" || e.slotDay === dayFilter);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Inscriptions (détail par élève)</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Toutes les inscriptions par <strong>créneau du planning</strong>, avec leur statut de paiement. Cliquez sur « Changer de créneau » pour déplacer un élève.</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap mb-2">
        {[
          { v: "all", label: `Toutes (${items.length})` },
          { v: "pending", label: `En attente (${items.filter((i) => i.paymentStatus === "pending").length})` },
          { v: "paid", label: `Payées (${items.filter((i) => i.paymentStatus === "paid").length})` },
          { v: "partial", label: `Partielles (${items.filter((i) => i.paymentStatus === "partial").length})` },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} className={`px-3 py-1.5 rounded-full text-sm ${filter === f.v ? "bg-coral text-white" : "bg-white text-stone-700 hover:bg-stone-100"}`} data-testid={`enrollments-filter-${f.v}`}>{f.label}</button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap mb-4 items-center">
        <span className="text-xs uppercase tracking-widest text-stone-500">Filtre jour :</span>
        {[{ v: "all", label: "Tous" }, ...DAY_ORDER_CRM.filter((d) => items.some((i) => i.slotDay === d)).map((d) => ({ v: d, label: DAY_LABELS_CRM[d] }))].map((f) => (
          <button key={f.v} onClick={() => setDayFilter(f.v)} className={`px-3 py-1 rounded-full text-xs ${dayFilter === f.v ? "bg-deep-rose text-white" : "bg-white text-stone-700 hover:bg-stone-100 border border-stone-200"}`} data-testid={`enrollments-day-${f.v}`}>{f.label}</button>
        ))}
      </div>
      {loading ? <div className="text-stone-500 p-8 text-center">Chargement...</div> : (() => {
        // Group filtered enrollments by studentId
        const byStudent = {};
        const order = [];
        for (const e of filtered) {
          const sid = e.studentId || `(no-student)-${e.id}`;
          if (!byStudent[sid]) {
            byStudent[sid] = { studentId: sid, studentFirstName: e.studentFirstName || "(élève inconnu)", familyId: e.familyId, items: [] };
            order.push(sid);
          }
          byStudent[sid].items.push(e);
        }
        if (order.length === 0) {
          return <div className="bg-white rounded-2xl p-12 text-center text-stone-500">Aucune inscription pour ce filtre.</div>;
        }
        return (
          <div className="space-y-4">
            {order.map((sid) => {
              const group = byStudent[sid];
              return (
                <StudentEnrollmentBlock
                  key={sid}
                  student={{ id: group.studentId, firstName: group.studentFirstName }}
                  enrollments={group.items}
                  onChange={load}
                />
              );
            })}
          </div>
        );
      })()}
    </div>
  );
};

// ============================ Payments view ============================
export const PaymentsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/payments").then(({ data }) => setItems(data || [])).catch(() => toast.error("Erreur")).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Paiements en ligne</h1>
      <p className="text-stone-500 mt-1 text-sm sm:text-base">Transactions Stripe (CB & 1ère mensualité).</p>
      <div className="mt-6">
        {loading ? <div className="text-stone-500 p-8 text-center">Chargement...</div> : (
          <PaymentsList payments={items} />
        )}
      </div>
    </div>
  );
};


// ============================ Family Edit Modal ============================
const Field = ({ label, value, onChange, type = "text", autoComplete, inputMode, placeholder, testId, ...rest }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      inputMode={inputMode}
      placeholder={placeholder}
      data-testid={testId}
      {...rest}
      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
    />
  </div>
);

const FamilyEditModal = ({ family, onClose, onSaved }) => {
  const [form, setForm] = useState({
    parentFirstName: family.parentFirstName || "",
    parentLastName: family.parentLastName || (!family.parentFirstName ? (family.parentName || "") : ""),
    email: family.email || "",
    phone: family.phone || "",
    address: family.address || "",
    postalCode: family.postalCode || "",
    city: family.city || "",
    notes: family.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.parentFirstName.trim() && !form.parentLastName.trim()) {
      return toast.error("Renseignez au moins un prénom ou un nom");
    }
    if (!form.email.trim()) return toast.error("Email obligatoire");
    setSaving(true);
    try {
      await api.patch(`/admin/families/${family.id}`, {
        parentFirstName: form.parentFirstName.trim(),
        parentLastName: form.parentLastName.trim(),
        parentName: `${form.parentFirstName.trim()} ${form.parentLastName.trim()}`.trim(),
        email: form.email.trim(),
        phone: form.phone,
        address: form.address,
        postalCode: form.postalCode,
        city: form.city,
        notes: form.notes,
      });
      toast.success("Famille mise à jour");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-xl max-w-xl w-full p-6 sm:p-8 my-8 relative" data-testid="family-edit-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200"><X size={18}/></button>
        <h3 className="font-serif-display text-2xl text-stone-800 mb-1">Modifier la famille</h3>
        <p className="text-stone-500 text-sm mb-5">Coordonnées du responsable et adresse postale.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Prénom" value={form.parentFirstName} onChange={set("parentFirstName")} autoComplete="given-name" testId="family-edit-first" />
          <Field label="Nom" value={form.parentLastName} onChange={set("parentLastName")} autoComplete="family-name" testId="family-edit-last" />
          <Field label="Email *" type="email" value={form.email} onChange={set("email")} autoComplete="email" inputMode="email" testId="family-edit-email" />
          <Field label="Téléphone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" inputMode="tel" testId="family-edit-phone" />
          <div className="sm:col-span-2">
            <Field label="Adresse" value={form.address} onChange={set("address")} autoComplete="street-address" testId="family-edit-address" />
          </div>
          <Field label="Code postal" value={form.postalCode} onChange={set("postalCode")} autoComplete="postal-code" inputMode="numeric" maxLength={5} testId="family-edit-postal" />
          <Field label="Ville" value={form.city} onChange={set("city")} autoComplete="address-level2" testId="family-edit-city" />
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Notes internes</label>
            <textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} data-testid="family-edit-notes" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-4 border-t border-stone-100">
          <button onClick={onClose} className="px-5 py-2 rounded-full bg-stone-100 text-stone-700 text-sm">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="family-edit-save" className="px-6 py-2 rounded-full bg-coral text-white text-sm tracking-widest uppercase disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Save size={14}/> {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================ Student Edit Modal ============================
const StudentEditModal = ({ student, familyId, onClose, onSaved }) => {
  const isNew = !student;
  const [form, setForm] = useState({
    firstName: student?.firstName || "",
    lastName: student?.lastName || "",
    birthDate: student?.birthDate || "",
    age: student?.age || "",
    notes: student?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.firstName.trim()) return toast.error("Le prénom est obligatoire");
    setSaving(true);
    try {
      if (isNew) {
        await api.post(`/admin/families/${familyId}/students`, form);
        toast.success("Élève ajouté");
      } else {
        await api.patch(`/admin/students/${student.id}`, form);
        toast.success("Élève mis à jour");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-xl max-w-lg w-full p-6 sm:p-8 my-8 relative" data-testid="student-edit-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200"><X size={18}/></button>
        <h3 className="font-serif-display text-2xl text-stone-800 mb-1">{isNew ? "Nouvel élève" : `Modifier ${student.firstName}`}</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <Field label="Prénom *" value={form.firstName} onChange={set("firstName")} testId="student-edit-first" />
          <Field label="Nom" value={form.lastName} onChange={set("lastName")} testId="student-edit-last" />
          <Field label="Date de naissance" type="date" value={form.birthDate} onChange={set("birthDate")} testId="student-edit-birth" />
          <Field label="Âge" value={form.age} onChange={set("age")} inputMode="numeric" testId="student-edit-age" />
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Notes (allergies, niveau, etc.)</label>
            <textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="student-edit-notes" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-4 border-t border-stone-100">
          <button onClick={onClose} className="px-5 py-2 rounded-full bg-stone-100 text-stone-700 text-sm">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="student-edit-save" className="px-6 py-2 rounded-full bg-coral text-white text-sm tracking-widest uppercase disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Save size={14}/> {saving ? "Enregistrement..." : (isNew ? "Ajouter" : "Enregistrer")}
          </button>
        </div>
      </div>
    </div>
  );
};
